import React, { useMemo, useState } from 'react'
import { Box, Button, InputAdornment, Skeleton, TablePagination, TableSortLabel, TextField, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { IconSeeker } from '@ui-seeker/core'

import { AppAntSwitch, AppBrandMark, AppDialog, AppDialogBody, AppDialogCancel, AppDialogFooter, AppDialogHeader, AppEmptyLine, AppNote, AppPillToggle, secondaryButtonSx } from '@components'
import { dsColorNew } from '@palette'
import { CurveChart } from '../../../@components/PriceCurve'
import { SERIES_COLORS } from '../../../@services/curve'
import { addDays, fmtWeekday, fromIso } from '../../../@services/dates'
import { money } from '../../../@services/format'
import { providerName } from '../../../@services/providerName'
import { Change } from './Cards'


const TOP = 5
const NONE = Object.freeze({})
const tableSx = {
	width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13,
	'& th': { backgroundColor: dsColorNew.tableHead, color: 'rgba(255,255,255,.92)', fontSize: 13, fontWeight: 700, textAlign: 'right', px: 1.5, py: 0.75, whiteSpace: 'nowrap' },
	'& th:first-of-type': { textAlign: 'left' },
	'& td': { px: 1.5, py: 0.9, borderBottom: `1px solid ${dsColorNew.grayBorder}`, color: dsColorNew.textDark, fontVariantNumeric: 'tabular-nums', textAlign: 'right' },
	'& td:first-of-type': { textAlign: 'left' },
	'& .MuiTableSortLabel-root, & .MuiTableSortLabel-root:hover, & .MuiTableSortLabel-root.Mui-active, & .MuiTableSortLabel-icon': { color: 'rgba(255,255,255,.92) !important' },
}

const Loading = () => <Skeleton variant="rounded" height={230} sx={{ borderRadius: '10px', transform: 'none' }}/>
const Failed = ({ onRetry }) => <AppNote severity="error"><b>Could not load the prices.</b>
	<Box sx={{ mt: 1 }}><Button variant="outlined" onClick={onRetry} sx={secondaryButtonSx}>Try again</Button></Box></AppNote>

/** CA 15a: a provider's curve split by category or Acriss — the 5 with more prices, the rest behind a switch */
export function ProviderDetailDialog({ open, name, split, onSplit, state, stat, code, today, onExplorer, onClose }) {
	const [others, setOthers] = useState(false)
	const [hover, setHover] = useState(null)
	const data = state.data
	const lines = useMemo(() => (data?.lines || []).map((l, i) => ({
		key: `s${i}`, name: l.name || '—', role: i < TOP ? 'main' : 'other', color: i < TOP ? SERIES_COLORS[i % SERIES_COLORS.length] : '#CBD2DC', width: i < TOP ? 2 : 1.25,
		n: l.n, points: l.points.filter(p => p[stat] !== null && p[stat] !== undefined).map(p => ({ d: p.d, value: p[stat], n: p.n, thin: p.n < 5, captured: p.captured })),
	})), [data, stat])
	const chart = useMemo(() => (data ? { days: data.days, main: lines.slice(0, TOP), others: lines.slice(TOP), single: (data.pickupDates || []).length === 1, pickupDates: data.pickupDates } : null), [data, lines])
	const kind = split === 'acriss' ? 'Acriss' : 'category'
	const rowOf = l => {
		const sorted = [...l.points].sort((a, b) => a.d - b.d)
		const now = sorted[0]
		const before = now ? sorted.filter(p => p.d >= now.d + 7)[0] : null
		return { l, now, before, eur: now && before ? now.value - before.value : null, pct: now && before && before.value ? now.value / before.value - 1 : null }
	}

	return <AppDialog open={open} onClose={onClose} maxWidth="md" aria-labelledby="pc-provider-title">
		<AppDialogHeader icon="car" title={<span id="pc-provider-title">{name} by {kind}</span>} subtitle="Current period · each point is a capture day" onClose={onClose}/>
		<AppDialogBody sx={{ maxHeight: 'calc(100vh - 260px)', overflow: 'auto' }}>
			<AppPillToggle size="small" aria-label="Split by" value={split} onChange={onSplit} sx={{ mb: 1.5 }}
				options={[{ value: 'category', label: 'Category' }, { value: 'acriss', label: 'Acriss' }]}/>
			{state.loading ? <Loading/> : state.error ? <Failed onRetry={state.retry}/> : !lines.length ? <Box sx={{ py: 3 }}><AppEmptyLine>No prices of this provider in this curve.</AppEmptyLine></Box> : <>
				{lines.length > TOP && <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
					<AppAntSwitch checked={others} onChange={() => setOthers(v => !v)} inputProps={{ role: 'switch', 'aria-label': `Other ${split === 'acriss' ? 'Acriss' : 'categories'}` }}/>
					<Typography sx={{ fontSize: 12.5 }}>Other {split === 'acriss' ? 'Acriss' : 'categories'} ({lines.length - TOP})</Typography>
				</Box>}
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, fontSize: 12, mb: 0.5 }}>
					{lines.slice(0, TOP).map(l => <Box key={l.key} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}><Box component="i" sx={{ width: 12, height: 3, backgroundColor: l.color, display: 'inline-block' }}/>{l.name}</Box>)}
				</Box>
				<CurveChart curve={chart} hidden={NONE} showPrev={false} showOthers={others} code={code} marker={null} zone={null} hover={hover} onHover={setHover}
					label={`${name} by ${kind}`} height={230}/>
				<Box component="table" sx={{ ...tableSx, mt: 2 }}>
					<thead><tr><th>{split === 'acriss' ? 'Acriss' : 'Category'}</th><th>Last capture</th><th>7 days before</th><th>Change</th><th>Prices</th></tr></thead>
					<tbody>{lines.map(rowOf).map(r => <tr key={r.l.key}>
						<td>{r.l.name}</td><td>{r.now ? money(r.now.value, code) : '—'}</td><td>{r.before ? money(r.before.value, code) : '—'}</td>
						<td>{r.eur === null ? '—' : <Change eur={r.eur} pct={r.pct} code={code} size={12}/>}</td><td>{r.l.n}</td>
					</tr>)}</tbody>
				</Box>
			</>}
		</AppDialogBody>
		<AppDialogFooter>
			<Button variant="outlined" onClick={onExplorer} startIcon={<IconSeeker icon="sliders" sx={{ fontSize: 13 }}/>} sx={secondaryButtonSx}>Open in Advanced (BI)</Button>
			<Box sx={{ flex: 1 }}/>
			<AppDialogCancel onClick={onClose}>Close</AppDialogCancel>
		</AppDialogFooter>
	</AppDialog>
}

const COLUMNS = [['provider', 'Provider'], ['category', 'Category'], ['acriss', 'Acriss'], ['site', 'Site'], ['price', 'Price'], ['n', 'Prices']]

/** CA 15b: the prices behind a point: provider × category × Acriss × site, with how many prices; day before / after */
export function DayDetailDialog({ open, d, days, pickupDates, state, stat, code, identity, onDay, onExplorer, onClose }) {
	const [search, setSearch] = useState('')
	const [sort, setSort] = useState({ by: 'price', dir: 'asc' })
	const [page, setPage] = useState(0)
	const data = state.data
	const single = (pickupDates || []).length === 1
	const captured = single && fromIso(pickupDates[0]) ? addDays(fromIso(pickupDates[0]), -d) : null
	const title = captured ? `Prices captured on ${fmtWeekday(captured, false)}` : `Prices captured ${d} day${d === 1 ? '' : 's'} before each pick-up`
	const own = new Set(identity ? [identity.primaryId, ...(identity.otherIds || [])].map(Number) : [])

	const rows = useMemo(() => {
		const q = search.trim().toLowerCase()
		const list = (data?.rows || []).map(r => ({ ...r, provider: providerName(r.provider, r.providerId), price: r[stat] }))
			.filter(r => !q || [r.provider, r.category, r.acriss, r.site].some(v => String(v || '').toLowerCase().includes(q)))
		const k = sort.by
		list.sort((a, b) => {
			const x = a[k], y = b[k]
			const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x ?? '').localeCompare(String(y ?? ''))
			return sort.dir === 'asc' ? c : -c
		})
		return list
	}, [data, search, sort, stat])
	const s = data?.summary

	return <AppDialog open={open} onClose={onClose} maxWidth="md" aria-labelledby="pc-day-title">
		<AppDialogHeader icon="calendar" title={<span id="pc-day-title">{title}</span>}
			subtitle={`${d === 0 ? 'Pick-up day' : `${d} day${d === 1 ? '' : 's'} before pick-up`}${s ? ` · ${s.prices} prices from ${s.providers} providers on ${s.sites} sites` : ''}`} onClose={onClose}/>
		<AppDialogBody sx={{ maxHeight: 'calc(100vh - 260px)', overflow: 'auto' }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
				<Button variant="outlined" size="small" disabled={d >= days} onClick={() => onDay(d + 1)} sx={secondaryButtonSx}>‹ Day before</Button>
				<Button variant="outlined" size="small" disabled={d <= 0} onClick={() => onDay(d - 1)} sx={secondaryButtonSx}>Day after ›</Button>
				<Box sx={{ flex: 1 }}/>
				<TextField size="small" placeholder="Find a provider, Acriss, category or site" value={search}
					onChange={e => { setSearch(e.target.value); setPage(0) }} onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); setSearch('') } }}
					inputProps={{ 'aria-label': 'Find a provider, Acriss, category or site' }} sx={{ width: 320 }}
					InputProps={{ startAdornment: <InputAdornment position="start"><IconSeeker icon="magnifying-glass" sx={{ fontSize: 13 }}/></InputAdornment> }}/>
			</Box>
			{state.loading ? <Loading/> : state.error ? <Failed onRetry={state.retry}/> : !rows.length ? <Box sx={{ py: 3 }}><AppEmptyLine>{search ? 'Nothing matches.' : 'No prices captured that day.'}</AppEmptyLine></Box> : <>
				<Box component="table" sx={tableSx}>
					<thead><tr>{COLUMNS.map(([k, label]) => <th key={k}>
						<TableSortLabel active={sort.by === k} direction={sort.by === k ? sort.dir : 'asc'}
							onClick={() => setSort(cur => ({ by: k, dir: cur.by === k && cur.dir === 'asc' ? 'desc' : 'asc' }))}>{label}</TableSortLabel>
					</th>)}</tr></thead>
					<tbody>{rows.slice(page * 25, page * 25 + 25).map((r, i) => <Box component="tr" key={`${r.providerId}-${r.category}-${r.acriss}-${r.siteId}-${r.pickup}-${i}`}
						sx={{ backgroundColor: own.has(Number(r.providerId)) ? alpha(dsColorNew.appBlue, 0.05) : 'transparent' }}>
						<td><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}><AppBrandMark name={r.provider} size={14}/>{r.provider}</Box></td>
						<td>{r.category || '—'}</td><td>{r.acriss || '—'}</td><td>{r.site}</td><td>{money(r.price, code)}</td><td>{r.n}</td>
					</Box>)}</tbody>
				</Box>
				<TablePagination component="div" count={rows.length} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={25} rowsPerPageOptions={[25]}/>
			</>}
		</AppDialogBody>
		<AppDialogFooter>
			<Button variant="outlined" onClick={onExplorer} startIcon={<IconSeeker icon="sliders" sx={{ fontSize: 13 }}/>} sx={secondaryButtonSx}>Open this day in Advanced (BI)</Button>
			<Box sx={{ flex: 1 }}/>
			<AppDialogCancel onClick={onClose}>Close</AppDialogCancel>
		</AppDialogFooter>
	</AppDialog>
}
